// @greenlight-spec: 28-it-dashboard-ui.md

/**
 * Reads the `client_secret_*.json` Google Cloud downloads when an OAuth client
 * is created. A Web application client nests its values under `web`; a Desktop
 * app client nests them under `installed` and cannot serve a browser sign-in.
 */

export type GoogleOAuthClientFile =
  | {
      ok: true;
      clientId: string;
      clientSecret: string;
      /** The install's callback URL when the file's redirect URIs omit it. */
      missingRedirectUri: string | null;
    }
  | { ok: false; error: string };

export const DESKTOP_CLIENT_ERROR =
  'This is a Desktop app client. Create a Web application client instead.';
export const NOT_OAUTH_CLIENT_ERROR = "This isn't an OAuth client file from Google Cloud.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

export function parseGoogleOAuthClientFile(
  text: string,
  redirectUri: string,
): GoogleOAuthClientFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: NOT_OAUTH_CLIENT_ERROR };
  }
  if (!isRecord(parsed)) return { ok: false, error: NOT_OAUTH_CLIENT_ERROR };

  const web = parsed.web;
  if (!isRecord(web)) {
    return {
      ok: false,
      error: isRecord(parsed.installed) ? DESKTOP_CLIENT_ERROR : NOT_OAUTH_CLIENT_ERROR,
    };
  }

  const clientId = nonEmptyString(web.client_id);
  const clientSecret = nonEmptyString(web.client_secret);
  if (!clientId || !clientSecret) return { ok: false, error: NOT_OAUTH_CLIENT_ERROR };

  // Google compares redirect URIs exactly, so this check does too.
  const redirectUris = Array.isArray(web.redirect_uris) ? web.redirect_uris : [];
  const registered = redirectUris.some((uri) => uri === redirectUri);

  return {
    ok: true,
    clientId,
    clientSecret,
    missingRedirectUri: registered ? null : redirectUri,
  };
}
